import { SxProps } from "@mui/material";
import React, { useCallback } from "react";
import { BasicOption } from "../../components/universal/BaseAsyncAutocomplete";
import InfiniteScrollAutocomplete, {
  GetQueryVariablesFromPaginationAndInputArgs,
} from "../../components/universal/InfiniteScroll/InfiniteScrollAutocomplete";
import {
  MealPlanTemplateOwnerPreviewFragment,
  OrgGroupMealPlanTemplateOwnerCursorConnectionQueryVariables,
  useOrgGroupMealPlanTemplateOwnerCursorConnectionQuery,
} from "../../types";

interface ManagedByMultiSelectProps {
  selectedUsers: MealPlanTemplateOwnerPreviewFragment[];
  searchText: string;
  onChangeSelectedUsers: (users: MealPlanTemplateOwnerPreviewFragment[]) => void;
  sx?: SxProps;
}

export const ManagedByMultiSelect = ({ onChangeSelectedUsers, selectedUsers, searchText, sx }: ManagedByMultiSelectProps) => {
  const getQueryVariables = useCallback(
    ({ cursor, limit, input }: GetQueryVariablesFromPaginationAndInputArgs) => ({
      variables: {
        query: input,
        pagination: { cursor, limit },
        searchText,
      },
    }),
    [searchText]
  );

  const transformAndFilterOptions = useCallback(
    (edges: MealPlanTemplateOwnerPreviewFragment[] | undefined): MealPlanTemplateOwnerPreviewFragment[] => {
      const selectedUserIds = selectedUsers.map(u => u.id);
      return edges?.filter(u => !selectedUserIds.includes(u.id)) || [];
    },
    [selectedUsers]
  );

  return (
    <InfiniteScrollAutocomplete<
      "orgGroupMealPlanTemplateOwnerCursorConnection",
      MealPlanTemplateOwnerPreviewFragment,
      OrgGroupMealPlanTemplateOwnerCursorConnectionQueryVariables,
      MealPlanTemplateOwnerPreviewFragment,
      true,
      undefined
    >
      sx={sx}
      multiple
      getOptionSelected={usersAreEqual}
      disableCloseOnSelect
      blurOnSelect={false}
      showStartAdornment={false}
      disableRootPadding
      edgesAreEqual={usersAreEqual}
      queryKey="orgGroupMealPlanTemplateOwnerCursorConnection"
      useCursorConnectionQuery={useOrgGroupMealPlanTemplateOwnerCursorConnectionQuery}
      getQueryVariablesFromPaginationAndInput={getQueryVariables}
      transformAndFilterOptions={transformAndFilterOptions}
      renderOption={user => <BasicOption element={getUserLabel(user)} />}
      handleChange={(_, users) => onChangeSelectedUsers(users)}
      getOptionLabel={getUserLabel}
      getCustomOptionKey={(u: MealPlanTemplateOwnerPreviewFragment) => u.id}
      noOptionsText="No Users Found"
      inputPlaceholder="Managed By"
      value={selectedUsers}
      size="medium"
    />
  );
};

const getUserLabel = (user: MealPlanTemplateOwnerPreviewFragment): string => {
  return `${user.firstName} ${user.lastName}`;
};

const usersAreEqual = (user1: MealPlanTemplateOwnerPreviewFragment, user2: MealPlanTemplateOwnerPreviewFragment) => {
  return user1.id === user2.id;
};
